import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import { Workflow } from "lucide-react";
import { Button } from "@components/ui/button";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { EXCLUDE_KEYS, handlerStyle } from "../common/commonData";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import CustomSelect from "@components/common/CustomSelect";
import { executeWorkflow, getAllWorkflows } from "../createPluginApis";
import { useQuery } from "react-query";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

const MODE_OPTIONS = [
  { label: "Run once with all items", value: "once" },
  { label: "Run once for each item", value: "each" },
];

interface ConnectFlowNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const ConnectFlowNode: React.FC<NodeProps<ConnectFlowNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || data.header || "Connect Flow";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Node state
  const [workflowId, setWorkflowId] = useState("");
  const [mode, setMode] = useState("once");

  // Response state
  const [response, setResponse] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Input data from connected nodes
  const [inputData, setInputData] = useState<any[]>([]);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Get pluginId from session
  const pluginId =
    sessionStorage.getItem("meshPluginId") ||
    sessionStorage.getItem("editPluginId") ||
    "";

  // Fetch all workflows from API
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ["getAllWorkflows", pluginId],
    queryFn: () => getAllWorkflows(pluginId),
    enabled: !!pluginId,
    staleTime: 0,
  });

  // Transform workflows data for CustomSelect
  const workflowOptions = React.useMemo(() => {
    if (!workflowsData?.data || !Array.isArray(workflowsData.data)) return [];

    // Get current active workflow to filter it out
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    let currentWorkflowUid = null;
    if (activeMeshFlowStr) {
      try {
        const activeMeshFlow = JSON.parse(activeMeshFlowStr);
        currentWorkflowUid = activeMeshFlow.uid;
      } catch (e) {
        console.error("Error parsing activeMeshFlow:", e);
      }
    }

    // Filter out current workflow and map to dropdown options
    return workflowsData.data
      .filter((workflow: any) => workflow.uid !== currentWorkflowUid)
      .map((workflow: any) => ({
        label: workflow.title || workflow.uid,
        value: workflow.uid,
      }));
  }, [workflowsData]);

  // Generate UID on mount if not exists
  useEffect(() => {
    if (!uid) {
      const generatedUid = `connect_flow_${Date.now()}`;
      setUid(generatedUid);
    }
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || data || {};

    if (params.workflow_id) setWorkflowId(params.workflow_id);
    if (params.mode) setMode(params.mode);

    // Load response if available
    if ((data as any).response) {
      setResponse((data as any).response);
    }
  }, [data]);

  // Sync title, description, and uid from data prop
  useEffect(() => {
    if (data.title) setTitle(data.title);
    if (data.description) setDescription(data.description);
    if (data.uid) setUid(data.uid);
  }, [data.title, data.description, data.uid]);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Get input data from connected nodes
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Auto-refresh when expanded
  useEffect(() => {
    if (!isExpanded || !reactFlowInstance) return;
    const interval = setInterval(() => {
      const edges = reactFlowInstance.getEdges();
      const nodes = reactFlowInstance.getNodes();
      setInputData(getConnectedInputs(id, edges, nodes));
    }, 1000);
    return () => clearInterval(interval);
  }, [isExpanded, reactFlowInstance, id]);

  // Ref to track previous response data
  const prevResponseDataRef = useRef<any>(null);

  // Ref to track previous stored parameters
  const prevStoredParametersRef = useRef<any>(null);

  // Sync response from node data
  useEffect(() => {
    const newResponseData = (data as any).response;

    if (!newResponseData) {
      if (response !== null && newResponseData === null) {
        prevResponseDataRef.current = null;
        setResponse(null);
      }
      return;
    }

    if (prevResponseDataRef.current === newResponseData) {
      return;
    }

    const newDataStr = JSON.stringify(newResponseData);
    const prevDataStr = prevResponseDataRef.current
      ? JSON.stringify(prevResponseDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevResponseDataRef.current = newResponseData;
      setResponse(newResponseData);
    } else {
      prevResponseDataRef.current = newResponseData;
    }
  }, [(data as any).response]);

  // Sync error from node data
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Store current node's parameters whenever they change
  useEffect(() => {
    if (!data.onDataChange || !uid) return;

    const storedParameters = {
      workflow_id: workflowId,
      mode: mode,
    };

    const storedParametersStr = JSON.stringify(storedParameters);
    const prevParametersStr = prevStoredParametersRef.current
      ? JSON.stringify(prevStoredParametersRef.current)
      : null;

    if (
      storedParametersStr !== prevParametersStr ||
      JSON.stringify(response) !== JSON.stringify(prevResponseDataRef.current)
    ) {
      prevStoredParametersRef.current = storedParameters;
      prevResponseDataRef.current = response;
      data.onDataChange(id, {
        storedParameters,
        workflow_id: workflowId,
        mode: mode,
        uid,
        title,
        description,
        response,
        outputData: response,
      });
    }
  }, [workflowId, mode, uid, title, description, id, response]);

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Copy response to clipboard
  const handleCopyResponse = () => {
    const dataToCopy = errorResponse || response;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Response copied to clipboard.",
      duration: 2000,
    });
  };

  // Execute connected workflow
  const executeConnectedWorkflow = async () => {
    if (!workflowId.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a workflow to connect",
        duration: 3000,
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the node",
        duration: 3000,
      });
      return;
    }

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first",
        duration: 4000,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    setResponse(null);
    setErrorResponse(null);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      const workflowNodes = executableNodes.map((node: any) => {
        const backendTypeInfo = getNodeBackendType(node.type || "custom");
        let nodeType = backendTypeInfo.type;
        let subType = backendTypeInfo.sub_type;

        if (node.data?.sub_type) {
          subType = node.data.sub_type;
          nodeType = node.data.type || "core";
        }

        const { parameters } = getNodeExecutionParameters(node, pluginId);

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};

      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = { main: [] };
          }

          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            setErrorResponse(
              currentNodeResult.error || "Workflow execution failed",
            );
            setResponse(null);
          } else if (currentNodeResult.output_data) {
            const outputData = currentNodeResult.output_data.map((item: any) =>
              item.json_data ? item.json_data : item,
            );
            setResponse(outputData);
            setErrorResponse(null);
          }
        }

        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: "Workflow executed successfully",
            duration: 3000,
          });
        }
      } else {
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                    response: result?.body?.data || result?.data || result,
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Workflow executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.message ||
                      result?.error ||
                      "Workflow execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Upstream node failed",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.message || result?.error || "Workflow execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Workflow Execution Error:", error);

      setErrorResponse({
        error: error?.message || "Workflow execution failed",
        details:
          error?.response?.data ||
          error?.stack ||
          "No additional details available",
      });
      setResponse(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Workflow execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Upstream node failed",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Workflow Error",
        description: error?.message || "Workflow execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    if (input.outputData) {
      if (Array.isArray(input.outputData)) {
        return input.outputData;
      }
      return input.outputData;
    }

    if (input.response) {
      if (Array.isArray(input.response)) {
        return input.response;
      }
      return input.response;
    }

    const displayData: any = {};
    if (input.configData) {
      const encryptedFields = input.configData?.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE_KEYS.includes(key))
        .forEach(([key, value]) => {
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }

    return displayData;
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData || inputData.length === 0) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          No input data available.
          <br />
          Connect a node to see data here.
        </div>
      );
    }

    const sources = inputData.map((input, index) => {
      let displayLabel = input.label || `Source ${index + 1}`;

      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData,
      };
    });

    return (
      <PaginatedInputViewer sources={sources} sourceCount={inputData.length} />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Workflow
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-pulse"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={38}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content - 3 Sections */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                placeholder="Enter node title"
                className="nodrag"
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Enter node description"
                className="w-full p-3 border rounded text-xs resize-none nodrag"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Parameters (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  <div className="space-y-4 mt-4">
                    {/* Workflow Selection */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium">
                        Select Workflow <span className="text-primary">*</span>
                      </label>
                      {isLoadingWorkflows ? (
                        <div className="text-xs text-muted-foreground p-2 border rounded-md bg-muted/20 text-center">
                          Loading workflows...
                        </div>
                      ) : workflowOptions.length === 0 ? (
                        <div className="text-xs text-muted-foreground p-2 border rounded-md bg-muted/20 text-center">
                          No workflows available
                        </div>
                      ) : (
                        <CustomSelect
                          data={workflowOptions}
                          defaultValue={workflowId}
                          onSelect={(value: string) => setWorkflowId(value)}
                          placeholder="Select a workflow to connect"
                          multiSelect={false}
                          size="sm"
                        />
                      )}
                    </div>

                    {/* Mode Selection */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium">
                        Execution Mode <span className="text-primary">*</span>
                      </label>
                      <CustomSelect
                        data={MODE_OPTIONS}
                        defaultValue={mode}
                        onSelect={(value: string) => setMode(value)}
                        placeholder="Select execution mode"
                        multiSelect={false}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="sticky -bottom-1 left-0 right-0 bg-background border-t mt-4">
                  <Button
                    onClick={executeConnectedWorkflow}
                    disabled={!workflowId.trim() || !title.trim() || loading}
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                  >
                    {loading ? "Executing..." : "Execute Workflow"}
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || response}
                  loading={loading}
                  onCopy={handleCopyResponse}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setResponse(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}

      {/* Handles */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />

      <Handle
        id="output"
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />
    </FullscreenWrapper>
  );
};

export default ConnectFlowNode;
